import { FC, useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { listWorkboardDirectories } from 'dashboard409a/services/dashboard';
import { DEFAULT_DROPBOX_ROOT } from '../dropbox.constants';
import { getDropboxFolderDisplayName } from '../dropbox.utils';
import { WorkboardFolderPickerProps } from './WorkboardFolderPicker.props';

// Searching the valuation root scans backup storage, so require a real query first.
const MIN_ROOT_QUERY_LENGTH = 2;
const DIRECTORY_STALE_TIME_IN_MS = 1000 * 60 * 5;

const normalizePickerPath = (path: string) => {
  const trimmed = path.trim();
  if (!trimmed) {
    return '';
  }
  return trimmed.endsWith('/') ? trimmed : `${trimmed}/`;
};

const directoryFromPath = (path: string) => {
  const normalizedPath = normalizePickerPath(path);
  const name = normalizedPath.split('/').filter(Boolean).at(-1) ?? '';
  return { name, path: normalizedPath };
};

const WorkboardFolderPicker: FC<WorkboardFolderPickerProps> = ({
  initialPrefix = '',
  dropboxRoot = DEFAULT_DROPBOX_ROOT,
  companyName,
  selectedPath,
  onSelect,
}) => {
  const [prefix, setPrefix] = useState(initialPrefix);
  const [folderFilter, setFolderFilter] = useState('');
  const [debouncedFilter, setDebouncedFilter] = useState('');
  const [draftSelection, setDraftSelection] = useState<{ name: string; path: string } | null>(null);
  const previousSelectedPathRef = useRef<string | null | undefined>(undefined);

  const segments = useMemo(() => prefix.split('/').filter(Boolean), [prefix]);
  const atRoot = segments.length === 0;

  useEffect(() => {
    setPrefix(initialPrefix);
  }, [initialPrefix]);

  useEffect(() => {
    if (selectedPath === previousSelectedPathRef.current) {
      return;
    }
    previousSelectedPathRef.current = selectedPath;

    if (!selectedPath?.trim()) {
      setDraftSelection(null);
      return;
    }

    const nextSelection = directoryFromPath(selectedPath);
    setDraftSelection(nextSelection);
    setFolderFilter(nextSelection.name);
  }, [selectedPath]);

  // Only seed the company name when nothing is selected yet. On mount this runs in the
  // same commit as the effect above, so `draftSelection` is still stale and cannot be
  // relied on to tell whether a saved path was just applied.
  useEffect(() => {
    if (atRoot && companyName && !draftSelection && !selectedPath?.trim()) {
      setFolderFilter(companyName);
    }
  }, [atRoot, companyName, draftSelection, selectedPath]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedFilter(folderFilter.trim()), 350);
    return () => clearTimeout(timer);
  }, [folderFilter]);

  const isRootQueryTooShort = atRoot && debouncedFilter.length < MIN_ROOT_QUERY_LENGTH;

  const { data: directories = [], isFetching: isLoadingDirs } = useQuery({
    queryKey: ['workboard-folder-picker', dropboxRoot, prefix, debouncedFilter],
    queryFn: () => listWorkboardDirectories({ prefix, q: debouncedFilter, root: dropboxRoot }),
    enabled: !isRootQueryTooShort,
    staleTime: DIRECTORY_STALE_TIME_IN_MS,
  });

  const selectedOption = useMemo(() => {
    if (!draftSelection) {
      return null;
    }

    const fromOptions = directories.find(
      directory => normalizePickerPath(directory.path) === normalizePickerPath(draftSelection.path)
    );
    return fromOptions ?? draftSelection;
  }, [directories, draftSelection]);

  const clearSelection = () => {
    setDraftSelection(null);
    setFolderFilter('');
    onSelect(null);
  };

  const navLabel = atRoot ? 'Search company folder' : 'Select valuation folder';

  const getNoOptionsText = () => {
    if (isRootQueryTooShort) {
      return `Type at least ${MIN_ROOT_QUERY_LENGTH} characters to search company folders`;
    }
    return atRoot ? 'No matching company folders in backup' : 'No valuation folders here';
  };

  const navigateToSegment = (index: number) => {
    clearSelection();
    if (index < 0) {
      setPrefix('');
      return;
    }
    setPrefix(`${segments.slice(0, index + 1).join('/')}/`);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Breadcrumbs maxItems={4} sx={{ mb: 1, '& .MuiBreadcrumbs-li': { maxWidth: 160 } }}>
        <Button
          variant="text"
          size="small"
          onClick={() => navigateToSegment(-1)}
          sx={{ minWidth: 0, p: 0, textTransform: 'none', fontWeight: 'normal' }}
        >
          {getDropboxFolderDisplayName(dropboxRoot)}
        </Button>
        {segments.map((segment, index) =>
          index < segments.length - 1 ? (
            <Button
              key={segment}
              variant="text"
              size="small"
              onClick={() => navigateToSegment(index)}
              sx={{
                minWidth: 0,
                p: 0,
                textTransform: 'none',
                fontWeight: 'normal',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {segment}
            </Button>
          ) : (
            <Typography key={segment} variant="body2" color="text.primary" noWrap>
              {segment}
            </Typography>
          )
        )}
      </Breadcrumbs>

      <Autocomplete
        size="small"
        value={selectedOption}
        inputValue={folderFilter}
        options={directories}
        loading={isLoadingDirs}
        getOptionLabel={option => option.name}
        isOptionEqualToValue={(option, value) =>
          normalizePickerPath(option.path) === normalizePickerPath(value.path)
        }
        filterOptions={items => items}
        onChange={(_event, option) => {
          if (option) {
            const nextSelection = {
              name: option.name,
              path: normalizePickerPath(option.path),
            };
            setDraftSelection(nextSelection);
            setFolderFilter(option.name);
            onSelect(nextSelection);
            return;
          }
          clearSelection();
        }}
        onInputChange={(_event, value, reason) => {
          if (reason === 'clear') {
            clearSelection();
            return;
          }
          if (reason === 'input') {
            setFolderFilter(value);
            if (draftSelection && value.trim() !== draftSelection.name) {
              setDraftSelection(null);
              onSelect(null);
            }
          }
        }}
        noOptionsText={getNoOptionsText()}
        renderOption={(props, option) => (
          <li {...props} key={option.path}>
            <Typography variant="body2" noWrap>
              {option.name}
            </Typography>
          </li>
        )}
        renderInput={params => (
          <TextField
            {...params}
            label={navLabel}
            placeholder={atRoot ? 'Type a company name…' : 'Type to filter valuation folders…'}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoadingDirs ? <CircularProgress size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Box>
  );
};

export default WorkboardFolderPicker;
